import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { WifiOff, RefreshCw } from 'lucide-react'
import { Button } from './button'
import { config } from '@/config/env'
import { canReachNetwork, isConnectionLost } from './network-failure'

interface NetworkErrorDialogProps {
  isOpen: boolean
  /** Resolves whether the network came back; false keeps the dialog up. */
  onRetry?: () => Promise<boolean>
}

/** Long enough for "Retrying..." to read as a check, not a flicker. */
const MIN_RETRY_MS = 800

export function NetworkErrorDialog({ isOpen, onRetry }: NetworkErrorDialogProps) {
  const [isRetrying, setIsRetrying] = useState(false)
  // A Try Again that found the network still down. Without saying so the
  // dialog looked exactly as before the click, as if nothing had happened.
  const [stillOffline, setStillOffline] = useState(false)

  useEffect(() => {
    if (!isOpen) setStillOffline(false)
  }, [isOpen])

  const handleRetry = async () => {
    setIsRetrying(true)
    setStillOffline(false)
    try {
      const [reachable] = await Promise.all([
        onRetry ? onRetry() : Promise.resolve(false),
        new Promise(resolve => setTimeout(resolve, MIN_RETRY_MS)),
      ])
      if (!reachable) setStillOffline(true)
    } finally {
      setIsRetrying(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          
          {/* Dialog */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-card dark:bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
            >
              {/* Header with animated error icon */}
              <div className="relative bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 p-6 text-center">
                <div className="relative inline-flex items-center justify-center">
                  {/* Pulsing background circle */}
                  <motion.div
                    animate={{ 
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="absolute w-20 h-20 bg-red-500/20 rounded-full"
                  />
                  
                  {/* Main error icon */}
                  <motion.div
                    animate={{ 
                      rotate: [0, -10, 10, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="relative z-10 w-16 h-16 bg-red-500 rounded-full flex items-center justify-center shadow-lg"
                  >
                    <WifiOff className="w-8 h-8 text-white" />
                  </motion.div>
                 
                </div>
               
              </div>

              {/* Content */}
              <div className="p-6">
                <h2 className="text-xl font-bold text-foreground mb-2 text-center">
                  Connection Lost
                </h2>
                <p className="text-muted-foreground text-center mb-6 leading-relaxed">
                  Unable to connect to the internet. Please check your network connection and try again.
                </p>

                <div role="status" aria-live="polite">
                  {stillOffline && (
                    <div className="flex items-center gap-2 mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-300">
                      <WifiOff className="w-4 h-4 shrink-0" />
                      <span>Network not available. Still unable to connect — check your connection, then try again.</span>
                    </div>
                  )}
                </div>
                
                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                    variant="outline"
                    onClick={() => window.location.reload()}
                    className="flex-1"
                  >
                    Reload Page
                  </Button>
                  <Button
                    onClick={handleRetry}
                    disabled={isRetrying}
                    className="flex-1 bg-primary hover:bg-primary/90 text-white font-medium"
                  >
                    {isRetrying ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Retrying...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Try Again
                      </>
                    )}
                  </Button>
                  
                
                </div>
                
                {/* Tips */}
                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    Troubleshooting Tips:
                  </h3>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Check your Wi-Fi or mobile data connection</li>
                    <li>• Try moving closer to your router</li>
                    <li>• Restart your router or modem</li>
                    <li>• Contact your internet service provider</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

/**
 * Opt a request out of the "Connection Lost" dialog because the caller reports
 * its own failure. An upload is the case: a proxy refusing a large body (nginx's
 * 413) sends no CORS headers, the browser reports that as a failed fetch, and
 * one oversized file blacked out the whole meeting as if the network had gone.
 * A real disconnect still raises the dialog through the `offline` event.
 */
export type SkipNetworkErrorInit = RequestInit & { skipNetworkError?: boolean }

// Hook to detect network status
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  // A page loaded while already offline never receives an `offline` event,
  // so it has to start with the dialog up.
  const [showDialog, setShowDialog] = useState(() => !navigator.onLine)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      setShowDialog(false)
    }

    const handleOffline = () => {
      setIsOnline(false)
      setShowDialog(true)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Also check for failed network requests. Some callers (e.g. the
    // Phoenix presence socket) are non-essential and opt out by setting
    // `x-skip-network-error: 1` — the failure shouldn't blackout the app.
    const originalFetch = window.fetch
    const shouldSkip = (init?: RequestInit | Request): boolean => {
      // A request that handles its own failure says so with a flag on the
      // init (see SkipNetworkErrorInit), not a header — a custom header on a
      // cross-origin call needs a preflight the API does not allow.
      if (init && (init as SkipNetworkErrorInit).skipNetworkError === true) return true
      const headers = init && 'headers' in init ? init.headers : undefined
      if (!headers) return false
      if (headers instanceof Headers) return headers.get('x-skip-network-error') === '1'
      if (Array.isArray(headers))
        return headers.some(([k, v]) => k.toLowerCase() === 'x-skip-network-error' && v === '1')
      // Plain object form.
      const obj = headers as Record<string, string>
      return obj['x-skip-network-error'] === '1' || obj['X-Skip-Network-Error'] === '1'
    }

    window.fetch = async (...args) => {
      const skip = shouldSkip(args[1])
      try {
        const response = await originalFetch(...args)
        if (!skip && isConnectionLost({ kind: 'response', status: response.status })) {
          setShowDialog(true)
        }
        return response
      } catch (error) {
        if (!skip && isConnectionLost({ kind: 'rejected', error })) setShowDialog(true)
        throw error
      }
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      window.fetch = originalFetch
    }
  }, [])

  // Closes the dialog only once a request actually gets through.
  const retry = async (): Promise<boolean> => {
    const reachable = await canReachNetwork(navigator.onLine, () => {
      const base = config.apiUrl || window.location.origin
      const init: SkipNetworkErrorInit = {
        method: 'HEAD',
        // Opaque is fine: any answer proves the network, and no-cors keeps a
        // cross-origin API from failing the probe on CORS alone.
        mode: 'no-cors',
        cache: 'no-store',
        signal: AbortSignal.timeout(8000),
        skipNetworkError: true,
      }
      return window.fetch(`${base}/?_netcheck=${Date.now()}`, init)
    })
    setIsOnline(reachable)
    if (reachable) setShowDialog(false)
    return reachable
  }

  return { isOnline, showDialog, setShowDialog, retry }
}
